/**
 * Группировка списка задач — макет 45:1832: заголовок группы со счётчиком и
 * задачи под ним.
 *
 * Считается на месте, а не на сервере: страница уже в руках, а группировка —
 * это способ на неё посмотреть, и запрос ради перестановки строк только моргал
 * бы таблицей.
 *
 * Здесь нет подписей: группа знает свой идентификатор (колонку, исполнителя,
 * приоритет), а как его назвать человеку — дело экрана, у которого есть
 * переводы и список колонок.
 */
import { PRIORITIES, type IssueRow } from './api'

import type { V1IssueType } from '@/api/clients/tracker-issue'

export type GroupBy = 'status' | 'assignee' | 'priority' | 'type' | 'none'

/** Порядок в списке выбора: статус первым — он же вид по умолчанию */
export const GROUP_OPTIONS: GroupBy[] = ['status', 'assignee', 'priority', 'type', 'none']

const TYPE_ORDER: V1IssueType[] = [
  'ISSUE_TYPE_EPIC',
  'ISSUE_TYPE_STORY',
  'ISSUE_TYPE_TASK',
  'ISSUE_TYPE_BUG',
  'ISSUE_TYPE_SUBTASK',
]

export interface IssueGroup {
  /** Колонка, исполнитель, приоритет или тип; пусто — «без него» */
  id: string
  issues: IssueRow[]
}

const keyOf = (issue: IssueRow, by: GroupBy): string => {
  switch (by) {
    case 'status':
      return issue.columnId
    case 'assignee':
      return issue.assigneeId
    case 'priority':
      return issue.priority
    case 'type':
      return issue.type
    default:
      return ''
  }
}

/**
 * Порядок групп задаёт не данные, а смысл: колонки идут слева направо, как на
 * доске, приоритеты — сверху вниз. Иначе группы прыгали бы местами от страницы
 * к странице, потому что их порядок задавала бы первая попавшаяся задача.
 */
const orderOf = (by: GroupBy, columnOrder: string[]): string[] => {
  switch (by) {
    case 'status':
      return columnOrder
    case 'priority':
      return PRIORITIES
    case 'type':
      return TYPE_ORDER
    default:
      return []
  }
}

export const groupIssues = (
  issues: IssueRow[],
  by: GroupBy,
  columnOrder: string[] = [],
): IssueGroup[] => {
  if (by === 'none') return issues.length ? [{ id: '', issues }] : []

  const buckets = new Map<string, IssueRow[]>()

  for (const issue of issues) {
    const key = keyOf(issue, by)
    const bucket = buckets.get(key)

    if (bucket) bucket.push(issue)
    else buckets.set(key, [issue])
  }

  const known = orderOf(by, columnOrder).filter((id) => buckets.has(id))
  // Всё, чему в порядке места не нашлось (исполнители, чужая колонка), идёт
  // следом в том виде, в каком приехало; «без исполнителя» — последним.
  const rest = [...buckets.keys()].filter((id) => id && !known.includes(id))
  const empty = buckets.has('') ? [''] : []

  return [...known, ...rest, ...empty].map((id) => ({ id, issues: buckets.get(id) ?? [] }))
}
